import type { FieldFormat } from '../../lib/fieldFormat'
import { labelsMatch } from './labelMatching'
import type { AgentBuilderReportData, DashboardWidget } from './types'
import { formatColumnValue, formatCompactColumnValue, formatFieldDisplayName } from './vizUtils'

export type GaugeValues = {
  actual: number | null
  target: number | null
  /** Dial maximum, calculated from the data: always above both the actual and the target. */
  ceiling: number
  /**
   * columnFormats keys (the returned column names) for the values, so stored formats apply. The
   * ceiling is on the actual's scale, so it shares the actual's key.
   */
  actualFormatKey: string | null
  targetFormatKey: string | null
  /** Catalog display names of the contract measures, for the legend. */
  actualName: string
  targetName: string
  /** The response's stored display formats, carried along for the formatting helpers below. */
  columnFormats: Record<string, FieldFormat> | null
}

/** The contract's two roles. Older JSON without the fields falls back to the first two requested measures. */
function getGaugeSlots(widget: DashboardWidget) {
  const requested = widget.daxGenerationRequest?.measures ?? []
  return { actual: widget.actualMeasure ?? requested[0], target: widget.targetMeasure ?? requested[1] }
}

/**
 * The measures a gauge queries, in order: the actual, then the target. A gauge cannot be plotted
 * against itself, so a target that names the actual's own measure is dropped; with no actual
 * nothing is queried, rather than letting the target stand in for it.
 */
export function getGaugeMeasures(widget: DashboardWidget): string[] {
  const { actual, target } = getGaugeSlots(widget)
  if (!actual?.trim()) return []
  return target?.trim() && target.trim().toLowerCase() !== actual.trim().toLowerCase() ? [actual, target] : [actual]
}

const PLAIN_NUMBER = /^[+-]?(\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i

/**
 * Parses numbers and display strings ("$1,200", "(45)", "12%", "1e5"); text without digits such as
 * "N/A" is null. Elsewhere a comma is a thousands separator, so a decimal comma ("1,5") reads as 15.
 */
export function toGaugeNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  const raw = String(value ?? '').trim()
  if (!/\d/.test(raw)) return null
  const parenNegative = /^\(.*\)$/.test(raw)
  const numeric = Number(PLAIN_NUMBER.test(raw) ? raw : raw.replace(/[^0-9.-]/g, ''))
  if (!Number.isFinite(numeric)) return null
  return parenNegative ? -Math.abs(numeric) : numeric
}

const CEILING_HEADROOM = 1.1
const CEILING_STEPS = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]

/**
 * The dial's maximum, calculated when the data arrives instead of being configured: the smallest
 * round number (1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6 or 8 times a power of ten) at least 10% above the
 * larger of actual and target, so the target mark and an over-target actual both stay on the dial.
 * With nothing positive to plot the dial is still valid, just empty, and runs 0 to 1.
 */
export function resolveGaugeCeiling(actual: number | null, target: number | null): number {
  const peak = Math.max(actual ?? 0, target ?? 0)
  if (peak <= 0) return 1
  const wanted = peak * CEILING_HEADROOM
  const magnitude = 10 ** Math.floor(Math.log10(wanted))
  const step = CEILING_STEPS.find((candidate) => candidate * magnitude >= wanted) ?? 10
  return step * magnitude
}

/** The catalog's display name for a contract measure value, found in the widget's own measures. */
function resolveMeasureName(widget: DashboardWidget, measure: string | undefined, fallback: string): string {
  if (!measure) return fallback
  const entry = (widget.measures ?? []).find((option) => labelsMatch(option.value, measure))
  return entry?.name?.trim() || formatFieldDisplayName(measure) || fallback
}

export function resolveGaugeValues(data: AgentBuilderReportData, widget: DashboardWidget): GaugeValues {
  const row = (data.rows?.[0] ?? null) as Record<string, unknown> | null
  const requested = getGaugeMeasures(widget)
  const { actual: actualMeasure, target: targetMeasure } = getGaugeSlots(widget)
  // The request asks for the measures above and a gauge has no dimensions, so the API returns
  // exactly those columns in that order: position is the mapping, no label matching. That only
  // holds for a response with exactly the requested columns; any other shape cannot be read
  // safely, so it reads as no data rather than risking a value under the wrong role.
  const columns = data.columns ?? []
  const readable = requested.length > 0 && columns.length === requested.length
  const actualColumn = readable ? columns[0] : undefined
  const targetColumn = readable && requested.length > 1 ? columns[1] : undefined
  const actual = row && actualColumn !== undefined ? toGaugeNumber(row[actualColumn]) : null
  const target = row && targetColumn !== undefined ? toGaugeNumber(row[targetColumn]) : null

  return {
    actual,
    target,
    ceiling: resolveGaugeCeiling(actual, target),
    actualFormatKey: actualColumn ?? actualMeasure ?? null,
    targetFormatKey: targetColumn ?? targetMeasure ?? null,
    actualName: resolveMeasureName(widget, actualMeasure, 'Actual'),
    targetName: resolveMeasureName(widget, targetMeasure, 'Target'),
    columnFormats: data.columnFormats ?? null,
  }
}

export type GaugeTone = 'pending' | 'success'

// Gauge look, shared by the on-screen dial (GaugeViz.tsx) and the PDF export.

/** Dial geometry in SVG viewBox units. Angles run 0° (left end of the arc) to 180° (right end). */
const DIAL_OUTER_RADIUS = 118
const DIAL_BAND_WIDTH = 18
const DIAL_ARC_RADIUS = DIAL_OUTER_RADIUS - DIAL_BAND_WIDTH / 2
export const GAUGE_DIAL = {
  viewWidth: 360,
  viewHeight: 190,
  // Room either side of the dial for a "Target $1.25M" label beside a tick near either end.
  edgePadding: 60,
  baselineY: 158,
  outerRadius: DIAL_OUTER_RADIUS,
  bandWidth: DIAL_BAND_WIDTH,
  arcRadius: DIAL_ARC_RADIUS,
  // The target tick spans the band, 5 units past each edge; its label sits outside the band.
  tickInnerRadius: DIAL_ARC_RADIUS - DIAL_BAND_WIDTH / 2 - 5,
  tickOuterRadius: DIAL_ARC_RADIUS + DIAL_BAND_WIDTH / 2 + 5,
  tickLabelRadius: DIAL_ARC_RADIUS + DIAL_BAND_WIDTH / 2 + 16,
} as const

export const GAUGE_COLORS = {
  tick: '#1d2433',
  track: '#edf0f3',
  headline: '#1d2433',
  targetLabel: '#475467',
  muted: '#5d5e59',
  scale: '#667085',
  divider: '#d4d0c8',
  met: '#1b8a5a',
} as const

export const GAUGE_PROGRESS_GRADIENT = { from: '#2a9d8f', to: '#00c7b7' } as const

export const GAUGE_TONES: Record<GaugeTone, { badgeBackground: string; badgeText: string; value: string }> = {
  pending: { badgeBackground: '#fbeedc', badgeText: '#5a380d', value: '#7f4e0e' },
  success: { badgeBackground: '#e4f4ee', badgeText: '#0f5a3c', value: '#166a48' },
}

export type GaugeProgress = {
  /** below: still short of the target; met: the gap vanishes at display precision; over: past it. */
  status: 'below' | 'met' | 'over'
  /** Whole-number share of the target reached; it passes 100 when the actual beats the target. */
  percentOfTarget: number
  /** Distance between actual and target, never negative: 0 once the target is met. */
  gap: number
}

/** How the actual stands against the target, or null without an actual and a positive target. */
export function resolveGaugeProgress({ actual, target, targetFormatKey, columnFormats }: GaugeValues): GaugeProgress | null {
  if (actual == null || target == null || target <= 0) return null
  const difference = actual - target
  const gap = Math.abs(difference)
  // A gap that reads as zero in the badge's own precision counts as meeting the target, so the
  // badge never says "$0 to go".
  const met = formatCompactColumnValue(gap, targetFormatKey, columnFormats) === formatCompactColumnValue(0, targetFormatKey, columnFormats)
  const share = Math.round((actual / target) * 100)
  return {
    status: met ? 'met' : difference > 0 ? 'over' : 'below',
    // Rounding must not put "100% of target" beside a "to go" or "over target" badge, so a share that
    // has not met the target stays on its own side of 100.
    percentOfTarget: met ? share : difference > 0 ? Math.max(share, 101) : Math.min(share, 99),
    gap: met ? 0 : gap,
  }
}

export type GaugeBadge = { tone: GaugeTone; text: string }

/** The gap-to-target pill: amber with "$212.7K to go", green once the target is met or beaten. */
export function resolveGaugeBadge(values: GaugeValues): GaugeBadge | null {
  const progress = resolveGaugeProgress(values)
  if (!progress) return null
  if (progress.status === 'met') return { tone: 'success', text: 'Target hit' }
  const amount = formatCompactColumnValue(progress.gap, values.targetFormatKey, values.columnFormats)
  return progress.status === 'over'
    ? { tone: 'success', text: `${amount} over target` }
    : { tone: 'pending', text: `${amount} to go` }
}

export type GaugeLegendEntry = { label: string; value: string; tone?: GaugeTone }

/**
 * The footer row, exact figures in each column's stored format: the contract's actual, its target,
 * and what is left. A beaten target flips the last cell to "Over target" so it never reads as a
 * negative remainder.
 */
export function buildGaugeLegend(values: GaugeValues): GaugeLegendEntry[] {
  const { actual, target, actualFormatKey, targetFormatKey, actualName, targetName, columnFormats } = values
  if (actual == null) return []

  const entries: GaugeLegendEntry[] = [{ label: actualName, value: formatColumnValue(actual, actualFormatKey, columnFormats) }]
  if (target != null) entries.push({ label: targetName, value: formatColumnValue(target, targetFormatKey, columnFormats) })

  const progress = resolveGaugeProgress(values)
  if (progress) {
    const value = formatColumnValue(progress.gap, targetFormatKey, columnFormats)
    entries.push(
      progress.status === 'over'
        ? { label: 'Over target', value, tone: 'success' }
        : { label: 'Remaining', value, tone: progress.status === 'met' ? 'success' : 'pending' },
    )
  }
  return entries
}
